/*
  Wall-clock to UTC, in a chosen timezone.

  The scheduler lets an operator pick a date, a time and a zone, and the past-time check compared
  `new Date('2026-09-12T09:00')` against now — which parses in the *browser's* zone, not the chosen
  one. Scheduling 09:00 Asia/Tokyo from London therefore either blocked a valid future time or
  admitted one already past, and the client and the server disagreed about which.

  This mirrors `api/email/lib/scheduleTimezone.ts`, deliberately and for its stated reason: the
  backend solves the same problem with the platform's own `Intl` rather than adding a timezone
  library for twenty lines of arithmetic, and `date-fns-tz` is in neither repository. The two must
  agree or the client will refuse times the server accepts.
*/

/**
 * The offset of a zone from UTC at a given instant, in milliseconds.
 *
 * Derived by asking `Intl` what the local clock reads at that instant and comparing. Positive east
 * of UTC. Necessarily instant-dependent, which is why it is a function and not a stored number —
 * an offset frozen at scheduling time is wrong the moment daylight saving shifts.
 *
 * @param instant - The moment to measure at.
 * @param timeZone - IANA zone name.
 * @returns Offset in milliseconds.
 */
function offsetMsAt(instant, timeZone) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  })
    .formatToParts(instant)
    .reduce((acc, part) => {
      if (part.type !== 'literal') acc[part.type] = part.value;
      return acc;
    }, {});

  const asUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second)
  );

  return asUtc - instant.getTime();
}

/**
 * Converts a wall-clock string in a zone to a UTC timestamp.
 *
 * @param wallClock - `YYYY-MM-DDTHH:mm`, as the date and time inputs produce.
 * @param timeZone - IANA zone the operator chose.
 * @returns Milliseconds since the epoch, or NaN when the input is not a wall clock.
 */
export function wallClockToUtc(wallClock, timeZone) {
  const match = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?$/.exec(wallClock ?? '');
  if (!match) return NaN;

  const [, year, month, day, hour, minute, second] = match;

  // First guess: read the wall clock as if it were UTC, then correct by the zone's offset at that
  // approximate instant. One correction is enough except within an hour of a DST transition, where
  // a second pass settles it.
  const guess = Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second ?? 0)
  );

  const firstPass = guess - offsetMsAt(new Date(guess), timeZone);
  return guess - offsetMsAt(new Date(firstPass), timeZone);
}

export default wallClockToUtc;
